import React, { Fragment, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, showConfirm, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─────────────────────────────────────────────────────────────────
// API config
// ─────────────────────────────────────────────────────────────────
const BASE_URL = WEBSERVICE;
const getMerchantId = () =>
  localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getAuthToken()}`,
  UUID: getAuthUuid() || "",
});

const api = {
  fetchCurrencies: (merchantId) =>
    fetch(`${BASE_URL}/MerchantDashboard/FetchUsdBtcLedgerAmount`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ merchant_id: String(merchantId) }),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  fetchAll: (merchantId) =>
    fetch(`${BASE_URL}/billing-profile/${merchantId}`, {
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  create: (payload) =>
    fetch(`${BASE_URL}/billing-profile/create`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  update: (payload) =>
    fetch(`${BASE_URL}/billing-profile/update`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  remove: (id, merchantId) =>
    fetch(`${BASE_URL}/billing-profile/delete/${id}/${merchantId}`, {
      method: "POST",
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  getAllGateways: (merchantId) =>
    fetch(`${BASE_URL}/merchant/getAllPaymentGateway`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ merchantId: String(merchantId) }),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  getPaymentMethods: () =>
    fetch(`${BASE_URL}/payment/getPaymentMethods`, {
      headers: authHeaders(),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),

  markAsDefault: (merchantId, profileId, value) =>
    fetch(`${BASE_URL}/billing-profile/markAsDefaultProfile`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({
        merchantId: String(merchantId),
        profileId: String(profileId),
        value,
      }),
    }).then((r) => {
      if (!r.ok) throw { response: { status: r.status } };
      return r.json();
    }),
};

// ─────────────────────────────────────────────────────────────────
// Dynamic currency arrays — populated from API on mount
// ─────────────────────────────────────────────────────────────────
let PAYBITO_CRYPTOS = [];
let ALL_PAYBITO_CODES = [];
let SUB_CRYPTOS = [];

const buildCryptoMaps = (currencies) => {
  const codeToId = {};
  const idToCode = {};
  currencies.forEach((c) => {
    codeToId[c.currency_code] = c.currency_id;
    idToCode[c.currency_id] = c.currency_code;
  });
  return { codeToId, idToCode };
};

const codesToIds = (codes, codeToId) =>
  codes
    .map((c) => codeToId[c])
    .filter(Boolean)
    .join(",");

const idsToCode = (idsStr, idToCode) => {
  if (!idsStr) return [];
  return idsStr
    .split(",")
    .map(Number)
    .map((id) => idToCode[id])
    .filter(Boolean);
};

// ─────────────────────────────────────────────────────────────────
// Map API record → UI form shape
// ─────────────────────────────────────────────────────────────────
// Reconstruct drag order arrays and enabled status from a saved paymentMethodIds string.
// Supports format "1:1:1,4:2:0,3:3:1" (id:position:status triples).
// Also handles paymentMethods array with a displayOrder field as fallback.
function parseOrdersFromRecord(rec) {
  let linearEntries = null; // [{ id, pos, status }]

  // Format: "1:1:1,2:2:0,3:3:1,4:4:0,5:5:1"
  const raw = rec.paymentMethodIds;
  if (raw && String(raw).includes(":")) {
    linearEntries = String(raw)
      .split(",")
      .map((p) => {
        const [id, pos, status] = p.split(":").map(Number);
        return { id, pos, status };
      })
      .filter(({ id, pos }) => !isNaN(id) && !isNaN(pos))
      .sort((a, b) => a.pos - b.pos);
  }
  // Fallback: paymentMethods array — sort by displayOrder, read status directly.
  else if (rec.paymentMethods && rec.paymentMethods.length > 0) {
    linearEntries = [...rec.paymentMethods]
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
      .map((m) => ({ id: m.id, pos: m.displayOrder || 0, status: m.status ?? 1 }));
  }

  if (!linearEntries) {
    return {
      methodOrder: ["stripe", "paypal", "crypto"],
      cryptoSubOrder: ["brandWallet", "externalWallet", "guestCheckout"],
      enabledFromIds: null,
    };
  }

  const methodOrder = [];
  const cryptoSubOrder = [];
  const enabledFromIds = {}; // id -> boolean
  let cryptoAdded = false;

  for (const { id, status } of linearEntries) {
    enabledFromIds[id] = status === 1;
    if (id === 1 && !methodOrder.includes("stripe")) methodOrder.push("stripe");
    else if (id === 2 && !methodOrder.includes("paypal")) methodOrder.push("paypal");
    else if (id === 7 && !methodOrder.includes("kurvpay")) methodOrder.push("kurvpay");
    else if (id === 8 && !methodOrder.includes("netbilling")) methodOrder.push("netbilling");
    else if (id === 9 && !methodOrder.includes("hms")) methodOrder.push("hms");
    else if (id === 10 && !methodOrder.includes("cardflo")) methodOrder.push("cardflo");
    else if (id === 11 && !methodOrder.includes("nmi")) methodOrder.push("nmi");
    else if ([3, 4, 5].includes(id) && !cryptoAdded) {
      methodOrder.push("crypto");
      cryptoAdded = true;
    }
    if (id === 3 && !cryptoSubOrder.includes("brandWallet")) cryptoSubOrder.push("brandWallet");
    else if (id === 4 && !cryptoSubOrder.includes("externalWallet")) cryptoSubOrder.push("externalWallet");
    else if (id === 5 && !cryptoSubOrder.includes("guestCheckout")) cryptoSubOrder.push("guestCheckout");
  }

  // Append any keys absent from saved data (so nothing is lost)
  if (!methodOrder.includes("stripe")) methodOrder.push("stripe");
  if (!methodOrder.includes("paypal")) methodOrder.push("paypal");
  if (!methodOrder.includes("kurvpay")) methodOrder.push("kurvpay");
  if (!methodOrder.includes("netbilling")) methodOrder.push("netbilling");
  if (!methodOrder.includes("hms")) methodOrder.push("hms");
  if (!methodOrder.includes("cardflo")) methodOrder.push("cardflo");
  if (!methodOrder.includes("nmi")) methodOrder.push("nmi");
  if (!methodOrder.includes("crypto")) methodOrder.push("crypto");
  if (!cryptoSubOrder.includes("brandWallet")) cryptoSubOrder.push("brandWallet");
  if (!cryptoSubOrder.includes("externalWallet")) cryptoSubOrder.push("externalWallet");
  if (!cryptoSubOrder.includes("guestCheckout")) cryptoSubOrder.push("guestCheckout");

  return { methodOrder, cryptoSubOrder, enabledFromIds };
}

function mapRecordToSingle(rec, idToCode) {
  const { methodOrder, cryptoSubOrder, enabledFromIds } = parseOrdersFromRecord(rec);

  // enabledFromIds is always populated (from paymentMethodIds string or paymentMethods array).
  const isEnabled = (id) => !!enabledFromIds?.[id];

  const hasBrandWallet = isEnabled(3);
  const hasExternalWallet = isEnabled(4);
  const hasGuestCheckout = isEnabled(5);
  const cryptoEnabled = hasBrandWallet || hasExternalWallet || hasGuestCheckout;
  const cryptos = idsToCode(rec.currencyIds, idToCode);

  return {
    id: rec.id,
    name: rec.profileName,
    customerEmail: rec.customerEmail || "",
    gateways: {
      stripe: { enabled: isEnabled(1) },
      paypal: { enabled: isEnabled(2) },
      kurvpay: { enabled: isEnabled(7) },
      netbilling: { enabled: isEnabled(8) },
      hms: { enabled: isEnabled(9) },
      cardflo: { enabled: isEnabled(10) },
      nmi: { enabled: isEnabled(11) },
      crypto: {
        enabled: cryptoEnabled,
        brandWallet: {
          enabled: hasBrandWallet,
          buyCryptoEnabled: rec.isBuyCryptoEnabled ? true : false,
        },
        externalWallet: {
          enabled: hasExternalWallet,
          providers: ["metamask", "tronlink", "trustwallet"],
        },
        guestCheckout: hasGuestCheckout,
        cryptos: cryptos.length ? cryptos : [...ALL_PAYBITO_CODES],
      },
    },
    processingFeeHandling: rec.isProcessingFeeApplied ? "customer" : "merchant",
    redirectUrl: rec.redirectUrl || "",
    isRecurring: !!rec.isRecurringBill,
    recurringEmail: rec.customerEmail || "",
    recurringPeriod: (rec.recurringPeriod || "MONTHLY").toLowerCase(),
    totalCycles: rec.totalCycles ? String(rec.totalCycles) : "",
    noEndDate: !rec.totalCycles,
    billingStartDate: "",
    retryAttempts: "3",
    isDefault: !!rec.isDefaultProfile,
    methodOrder,
    cryptoSubOrder,
  };
}

function mapRecordToSub(rec, idToCode) {
  const cryptos = idsToCode(rec.currencyIds, idToCode);
  return {
    id: rec.id,
    name: rec.profileName,
    availableCryptos: cryptos.length ? cryptos : SUB_CRYPTOS.map((c) => c.code),
    processingFeeHandling: rec.isProcessingFeeApplied ? "customer" : "merchant",
    deliveryMode: rec.customerEmail ? "request" : "form",
    customerEmail: rec.customerEmail || "",
    recurringPeriod: (rec.recurringPeriod || "MONTHLY").toLowerCase(),
    billingStartDate: rec.billingStartDate
      ? rec.billingStartDate.split(" ")[0]
      : "",
    totalCycles: rec.totalCycles ? String(rec.totalCycles) : "",
    noEndDate: !rec.totalCycles,
    retryAttempts: "3",
    redirectUrl: rec.redirectUrl || "",
  };
}

// ─────────────────────────────────────────────────────────────────
// Map UI form → API payload
// ─────────────────────────────────────────────────────────────────
function mapSingleToPayload(form, merchantId, codeToId) {
  const g = form.gateways || {};
  const c = g.crypto || {};
  const ext = c.externalWallet || {};

  return {
    id: form.id || 0,
    merchantId: parseInt(merchantId),
    billingType: "SINGLE",
    profileName: form.name,
    isPaybitoEnabled: c.enabled ? 1 : 0,
    isMetamaskEnabled:
      ext.enabled && ext.providers?.includes("metamask") ? 1 : 0,
    isTronlinkEnabled:
      ext.enabled && ext.providers?.includes("tronlink") ? 1 : 0,
    isBrandWalletEnabled: c.brandWallet?.enabled ? 1 : 0,
    isBuyCryptoEnabled: c.brandWallet?.buyCryptoEnabled ? 1 : 0,
    isGuestCheckoutEnabled: c.guestCheckout ? 1 : 0,
    isPaypalEnabled: g.paypal?.enabled ? 1 : 0,
    isStripeEnabled: g.stripe?.enabled ? 1 : 0,
    isProcessingFeeApplied: form.processingFeeHandling === "customer" ? 1 : 0,
    redirectUrl: form.redirectUrl || "",
    isRecurringBill: form.isRecurring ? 1 : 0,
    customerEmail: form.isRecurring ? form.recurringEmail || "" : "",
    recurringPeriod: (form.recurringPeriod || "monthly").toUpperCase(),
    totalCycles: form.noEndDate ? 0 : parseInt(form.totalCycles) || 0,
    billingStartDate: form.billingStartDate || null,
    isDefaultProfile: form.isDefault ? 1 : 0,
    currencyIds: codesToIds(c.cryptos || ALL_PAYBITO_CODES, codeToId),
  };
}

function mapSubToPayload(form, merchantId, codeToId) {
  return {
    id: form.id || 0,
    merchantId: parseInt(merchantId),
    billingType: "SUBSCRIPTION",
    profileName: form.name,
    isPaybitoEnabled: 1,
    isMetamaskEnabled: 0,
    isTronlinkEnabled: 0,
    isBrandWalletEnabled: 1,
    isGuestCheckoutEnabled: 0,
    isPaypalEnabled: 0,
    isStripeEnabled: 0,
    isProcessingFeeApplied: form.processingFeeHandling === "customer" ? 1 : 0,
    redirectUrl: form.redirectUrl || "",
    isRecurringBill: 1,
    customerEmail: null,
    recurringPeriod: (form.recurringPeriod || "monthly").toUpperCase(),
    totalCycles: form.noEndDate ? 0 : parseInt(form.totalCycles) || 0,
    billingStartDate: form.billingStartDate || null,
    isDefaultProfile: 0,
    currencyIds: codesToIds(
      form.availableCryptos || SUB_CRYPTOS.map((c) => c.code),
      codeToId
    ),
  };
}

// Builds "methodId:position:status" triples for ALL methods in drag-defined order.
// status = 1 if the method is toggled on, 0 if toggled off.
function buildPaymentMethodIds(g, methodOrder, cryptoSubOrder) {
  const pairs = [];
  let pos = 1;
  for (const key of methodOrder) {
    if (key === "stripe") {
      pairs.push(`1:${pos++}:${g.stripe?.enabled ? 1 : 0}`);
    } else if (key === "paypal") {
      pairs.push(`2:${pos++}:${g.paypal?.enabled ? 1 : 0}`);
    } else if (key === "kurvpay") {
      pairs.push(`7:${pos++}:${g.kurvpay?.enabled ? 1 : 0}`);
    } else if (key === "netbilling") {
      pairs.push(`8:${pos++}:${g.netbilling?.enabled ? 1 : 0}`);
    } else if (key === "hms") {
      pairs.push(`9:${pos++}:${g.hms?.enabled ? 1 : 0}`);
    } else if (key === "cardflo") {
      pairs.push(`10:${pos++}:${g.cardflo?.enabled ? 1 : 0}`);
    } else if (key === "nmi") {
      pairs.push(`11:${pos++}:${g.nmi?.enabled ? 1 : 0}`);
    } else if (key === "crypto") {
      for (const subKey of cryptoSubOrder) {
        if (subKey === "brandWallet")
          pairs.push(`3:${pos++}:${g.crypto?.brandWallet?.enabled ? 1 : 0}`);
        else if (subKey === "externalWallet")
          pairs.push(`4:${pos++}:${g.crypto?.externalWallet?.enabled ? 1 : 0}`);
        else if (subKey === "guestCheckout")
          pairs.push(`5:${pos++}:${g.crypto?.guestCheckout ? 1 : 0}`);
      }
    }
  }
  return pairs.join(",");
}

function mapSingleToCreatePayload(form, merchantId, codeToId) {
  const g = form.gateways || {};
  const c = g.crypto || {};
  return {
    merchantId: parseInt(merchantId),
    billingType: "SINGLE",
    profileName: form.name,
    paymentMethodIds: buildPaymentMethodIds(g, form.methodOrder, form.cryptoSubOrder),
    isBuyCryptoEnabled: c.brandWallet?.buyCryptoEnabled ? 1 : 0,
    redirectUrl: form.redirectUrl || "",
    customerEmail: form.customerEmail || "",
    isDefaultProfile: form.isDefault ? 1 : 0,
    currencyIds: codesToIds(c.cryptos || ALL_PAYBITO_CODES, codeToId),
  };
}

function mapSingleToUpdatePayload(form, merchantId, codeToId) {
  const g = form.gateways || {};
  const c = g.crypto || {};
  return {
    id: form.id,
    merchantId: parseInt(merchantId),
    billingType: "SINGLE",
    profileName: form.name,
    paymentMethodIds: buildPaymentMethodIds(g, form.methodOrder, form.cryptoSubOrder),
    isBuyCryptoEnabled: c.brandWallet?.buyCryptoEnabled ? 1 : 0,
    customerEmail: form.customerEmail || "",
    isDefaultProfile: form.isDefault ? 1 : 0,
    currencyIds: codesToIds(c.cryptos || ALL_PAYBITO_CODES, codeToId),
  };
}

// ─────────────────────────────────────────────────────────────────
// Style tokens
// ─────────────────────────────────────────────────────────────────
const S = {
  pageWrapper: { padding: "36px 24px 60px" },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "28px",
    flexWrap: "wrap",
    gap: "16px",
  },
  pageTitle: {
    fontSize: "24px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
  tabBar: {
    display: "flex",
    background: "var(--body-color-less)",
    borderRadius: "14px",
    padding: "5px",
    border: "1px solid var(--body-color-less2)",
    marginBottom: "28px",
    gap: "4px",
    width: "fit-content",
  },
  tab: {
    padding: "10px 28px",
    borderRadius: "10px",
    border: "none",
    background: "transparent",
    color: "var(--text-color)",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    opacity: 0.55,
    transition: "all 0.15s",
  },
  tabActive: {
    padding: "10px 28px",
    borderRadius: "10px",
    border: "none",
    background: "var(--btn-color)",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
    transition: "all 0.15s",
  },
  card: {
    background: "var(--body-color-less)",
    borderRadius: "16px",
    border: "1px solid var(--body-color-less2)",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    marginBottom: "24px",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px 12px",
    padding: "18px 20px",
    borderBottom: "1px solid var(--body-color-less2)",
  },
  cardHeaderIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "15px",
  },
  cardHeaderTitle: {
    fontSize: "15px",
    fontWeight: "700",
    color: "var(--text-color)",
    margin: 0,
  },
  cardBody: { padding: "22px 24px" },
  formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    color: "var(--text-color)",
    opacity: 0.7,
  },
  input: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  inputError: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid #ef4444",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    padding: "10px 14px",
    fontSize: "14px",
    borderRadius: "10px",
    border: "1px solid var(--body-color-less2)",
    background: "var(--body-color)",
    color: "var(--text-color)",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    appearance: "auto",
  },
  btnPrimary: {
    padding: "10px 24px",
    background: "var(--btn-color)",
    color: "#fff",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnOutline: {
    padding: "8px 18px",
    background: "transparent",
    border: "1px solid var(--btn-color)",
    color: "var(--btn-color)",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  btnDanger: {
    padding: "6px 14px",
    background: "rgba(239,68,68,0.1)",
    border: "none",
    color: "#ef4444",
    borderRadius: "8px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
  },
  sectionBox: {
    background: "var(--body-color)",
    borderRadius: "12px",
    padding: "18px 20px",
    border: "1px solid var(--body-color-less2)",
  },
  sectionTitle: {
    fontSize: "13px",
    fontWeight: "700",
    color: "var(--text-color)",
    marginBottom: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  errorText: { fontSize: "11px", color: "#ef4444", marginTop: "2px" },
  badge: {
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.3px",
  },
  emptyState: { textAlign: "center", padding: "52px 24px" },
  emptyIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: "24px",
  },
};

// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────
const EXTERNAL_WALLET_PROVIDERS = [
  {
    id: "metamask",
    label: "MetaMask",
    icon: "mdi-ethereum",
    color: "#f6851b",
    badge: "EVM",
  },
  {
    id: "tronlink",
    label: "TronLink",
    icon: "mdi-flash",
    color: "#ef0027",
    badge: "TRC-20",
  },
  {
    id: "trustwallet",
    label: "TrustWallet",
    icon: "mdi-shield-account",
    color: "#3375BB",
    badge: "Multi",
  },
];

const RECURRING_PERIODS = [
  {
    val: "weekly",
    label: "Weekly",
    icon: "mdi-calendar-week",
    maxCycles: 5200,
    unit: "weeks",
  },
  {
    val: "biweekly",
    label: "Bi-Weekly",
    icon: "mdi-calendar-range",
    maxCycles: 2600,
    unit: "bi-weeks",
  },
  {
    val: "monthly",
    label: "Monthly",
    icon: "mdi-calendar-month",
    maxCycles: 1200,
    unit: "months",
  },
  {
    val: "quarterly",
    label: "Quarterly",
    icon: "mdi-calendar-clock",
    maxCycles: 400,
    unit: "quarters",
  },
  {
    val: "yearly",
    label: "Yearly",
    icon: "mdi-calendar-star",
    maxCycles: 100,
    unit: "years",
  },
];

const getPeriodMeta = (val) =>
  RECURRING_PERIODS.find((p) => p.val === val) || RECURRING_PERIODS[2];

// ─────────────────────────────────────────────────────────────────
// Shared UI helpers
// ─────────────────────────────────────────────────────────────────
function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: "36px",
        height: "20px",
        borderRadius: "10px",
        background: value ? "var(--btn-color)" : "var(--body-color-less2)",
        position: "relative",
        transition: "background 0.2s",
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: "14px",
          height: "14px",
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: "3px",
          left: value ? "19px" : "3px",
          transition: "left 0.2s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }}
      />
    </div>
  );
}

function RadioDot({ active }) {
  return (
    <div
      style={{
        width: "16px",
        height: "16px",
        borderRadius: "50%",
        border: active
          ? "5px solid var(--btn-color)"
          : "2px solid rgba(128,128,128,0.4)",
        background: "var(--body-color)",
        flexShrink: 0,
        marginTop: "1px",
        transition: "all 0.15s",
      }}
    />
  );
}

function ProcessingFeeField({ value, onChange }) {
  const optStyle = (val) => ({
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px 14px",
    borderRadius: "10px",
    border:
      value === val
        ? "1.5px solid var(--btn-color)"
        : "1.5px solid var(--body-color-less2)",
    background:
      value === val
        ? "color-mix(in srgb, var(--btn-color), transparent 93%)"
        : "var(--body-color)",
    cursor: "pointer",
    transition: "all 0.15s",
  });
  return (
    <div style={S.formGroup}>
      <label style={S.label}>Processing Fee Handling</label>
      <div style={{ display: "flex", gap: "10px" }}>
        <div style={optStyle("customer")} onClick={() => onChange("customer")}>
          <RadioDot active={value === "customer"} />
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "var(--text-color)",
                lineHeight: "1.3",
              }}
            >
              Customer Pays Fee
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--text-color)",
                opacity: 0.5,
                marginTop: "2px",
              }}
            >
              Fee added on top of checkout total
            </div>
          </div>
        </div>
        <div style={optStyle("merchant")} onClick={() => onChange("merchant")}>
          <RadioDot active={value === "merchant"} />
          <div>
            <div
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "var(--text-color)",
                lineHeight: "1.3",
              }}
            >
              Merchant Absorbs Fee
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--text-color)",
                opacity: 0.5,
                marginTop: "2px",
              }}
            >
              Fee deducted from your payout
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ icon, label, value, color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <i
        className={`mdi ${icon}`}
        style={{
          fontSize: "14px",
          color: color || "var(--btn-color)",
          opacity: 0.75,
        }}
      />
      <span
        style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5 }}
      >
        {label}:
      </span>
      <span
        style={{
          fontSize: "12px",
          fontWeight: "600",
          color: color || "var(--text-color)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function EmptyState({ icon, color, title, subtitle, actionLabel, onAction }) {
  return (
    <div style={S.card}>
      <div style={S.emptyState}>
        <div
          style={{
            ...S.emptyIcon,
            background: `color-mix(in srgb, ${color} 12%, transparent)`,
            color,
          }}
        >
          <i className={`mdi ${icon}`} />
        </div>
        <div
          style={{
            fontSize: "15px",
            fontWeight: "600",
            color: "var(--text-color)",
            marginBottom: "6px",
          }}
        >
          {title}
        </div>
        <div
          style={{ fontSize: "13px", color: "var(--text-color)", opacity: 0.5 }}
        >
          {subtitle}
        </div>
        {actionLabel && onAction && (
          <button
            style={{ ...S.btnPrimary, marginTop: "18px" }}
            onClick={onAction}
          >
            <i className="mdi mdi-plus" />
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function RecurringCycleBlock({
  recurringPeriod,
  totalCycles,
  noEndDate,
  onChange,
}) {
  const meta = getPeriodMeta(recurringPeriod);
  const handleCyclesChange = (val) => {
    const n = parseInt(val);
    if (!isNaN(n) && n > meta.maxCycles)
      onChange({ totalCycles: String(meta.maxCycles) });
    else onChange({ totalCycles: val });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={S.formGroup}>
        <label style={S.label}>Recurring Period</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {RECURRING_PERIODS.map((p) => (
            <button
              key={p.val}
              type="button"
              onClick={() => {
                const curCycles = parseInt(totalCycles);
                onChange({
                  recurringPeriod: p.val,
                  totalCycles:
                    !isNaN(curCycles) && curCycles > p.maxCycles
                      ? String(p.maxCycles)
                      : totalCycles,
                });
              }}
              style={{
                padding: "8px 18px",
                borderRadius: "10px",
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.15s",
                border:
                  recurringPeriod === p.val
                    ? "1.5px solid var(--btn-color)"
                    : "1.5px solid var(--body-color-less2)",
                background:
                  recurringPeriod === p.val
                    ? "color-mix(in srgb, var(--btn-color) 12%, transparent)"
                    : "var(--body-color)",
                color:
                  recurringPeriod === p.val
                    ? "var(--btn-color)"
                    : "var(--text-color)",
              }}
            >
              <i className={`mdi ${p.icon}`} style={{ fontSize: "13px" }} />{" "}
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div style={S.formGroup}>
        <label style={S.label}>Total Billing Cycles</label>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <input
              type="number"
              min="1"
              max={meta.maxCycles}
              style={{
                ...S.input,
                paddingRight: "110px",
                opacity: noEndDate ? 0.4 : 1,
              }}
              value={totalCycles}
              onChange={(e) => handleCyclesChange(e.target.value)}
              placeholder={
                noEndDate
                  ? "No end date"
                  : `Max ${meta.maxCycles.toLocaleString()} ${meta.unit}`
              }
              disabled={noEndDate}
            />
            {!noEndDate && (
              <span
                style={{
                  position: "absolute",
                  right: "13px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: "var(--text-color)",
                  opacity: 0.4,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                }}
              >
                max {meta.maxCycles.toLocaleString()}
              </span>
            )}
          </div>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              userSelect: "none",
              flexShrink: 0,
              padding: "10px 16px",
              borderRadius: "10px",
              border: noEndDate
                ? "1.5px solid var(--btn-color)"
                : "1.5px solid var(--body-color-less2)",
              background: noEndDate
                ? "color-mix(in srgb, var(--btn-color) 8%, transparent)"
                : "var(--body-color)",
              transition: "all 0.15s",
            }}
          >
            <input
              type="checkbox"
              checked={noEndDate}
              onChange={(e) =>
                onChange({
                  noEndDate: e.target.checked,
                  totalCycles: e.target.checked ? "" : totalCycles,
                })
              }
              style={{
                width: "14px",
                height: "14px",
                accentColor: "var(--btn-color)",
                cursor: "pointer",
              }}
            />
            <span
              style={{
                fontSize: "12px",
                fontWeight: "600",
                color: noEndDate ? "var(--btn-color)" : "var(--text-color)",
                opacity: noEndDate ? 1 : 0.7,
              }}
            >
              No End Date
            </span>
          </label>
        </div>
        <div
          style={{
            fontSize: "11px",
            color: "var(--text-color)",
            opacity: 0.45,
            marginTop: "2px",
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <i
            className="mdi mdi-information"
            style={{ fontSize: "12px" }}
          />
          {noEndDate
            ? `Subscription runs indefinitely`
            : `Max ${meta.maxCycles.toLocaleString()} ${meta.unit
            } for ${meta.label.toLowerCase()} billing`}
        </div>
      </div>
    </div>
  );
}

function RedirectUrlField({ value, onChange, error }) {
  return (
    <div style={S.formGroup}>
      <label style={S.label}>After Payment Success — Redirect To</label>
      <div style={{ position: "relative" }}>
        <i
          className="mdi mdi-link-variant"
          style={{
            position: "absolute",
            left: "13px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "16px",
            color: "var(--text-color)",
            opacity: 0.35,
            pointerEvents: "none",
          }}
        />
        <input
          type="url"
          style={{ ...(error ? S.inputError : S.input), paddingLeft: "40px" }}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://yoursite.com/thank-you"
          autoComplete="off"
        />
      </div>
      {error ? (
        <span style={S.errorText}>{error}</span>
      ) : (
        <span
          style={{
            fontSize: "11px",
            color: "var(--text-color)",
            opacity: 0.45,
            marginTop: "2px",
          }}
        >
          Customer will be redirected here after a successful payment
        </span>
      )}
    </div>
  );
}

function VertexIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="4.5" r="2.5" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="4" cy="19.5" r="2.5" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="20" cy="19.5" r="2.5" fill="currentColor" fillOpacity="0.2" stroke="currentColor" strokeWidth="1.4" />
      <line x1="12" y1="7" x2="4.9" y2="17.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="12" y1="7" x2="19.1" y2="17.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="6.5" y1="19.5" x2="17.5" y2="19.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function TitanIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3L4 7V12.5C4 16.97 7.56 21.2 12 22C16.44 21.2 20 16.97 20 12.5V7L12 3Z" fill="currentColor" fillOpacity="0.18" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M9 12.5L11.5 15L15.5 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MethodRow({ icon, customIcon, color, label, sublabel, badge, enabled, onToggle }) {
  return (
    <div
      style={{
        borderRadius: "14px",
        border: enabled
          ? `1.5px solid color-mix(in srgb, ${color} 35%, transparent)`
          : "1.5px solid var(--body-color-less2)",
        background: enabled
          ? `color-mix(in srgb, ${color} 5%, var(--body-color-less))`
          : "var(--body-color-less)",
        transition: "all 0.15s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "14px",
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "11px",
            background: `color-mix(in srgb, ${color} 15%, transparent)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
            fontSize: "18px",
            flexShrink: 0,
          }}
        >
          {customIcon ? customIcon : <i className={`mdi ${icon}`} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 8px" }}>
            <span
              style={{
                fontSize: "13px",
                fontWeight: "700",
                color: "var(--text-color)",
              }}
            >
              {label}
            </span>
            <span
              style={{
                ...S.badge,
                background: `color-mix(in srgb, ${color} 15%, transparent)`,
                color,
              }}
            >
              {badge}
            </span>
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.5,
              marginTop: "2px",
              wordBreak: "break-word",
            }}
          >
            {sublabel}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: "var(--text-color)",
              opacity: enabled ? 0.8 : 0.4,
            }}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <Toggle value={enabled} onChange={onToggle} />
        </div>
      </div>
    </div>
  );
}

function SubMethodRow({ icon, color, label, desc, enabled, onToggle }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 14px",
        borderRadius: "10px",
        border: enabled
          ? `1.5px solid color-mix(in srgb, ${color} 30%, transparent)`
          : "1.5px solid var(--body-color-less2)",
        background: enabled
          ? `color-mix(in srgb, ${color} 6%, var(--body-color))`
          : "var(--body-color)",
        transition: "all 0.15s",
      }}
    >
      <div
        style={{
          width: "30px",
          height: "30px",
          borderRadius: "8px",
          background: `color-mix(in srgb, ${color} 12%, transparent)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          fontSize: "15px",
          flexShrink: 0,
        }}
      >
        <i className={`mdi ${icon}`} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: "700",
            color: "var(--text-color)",
            wordBreak: "break-word",
          }}
        >
          {label}
        </div>
        <div
          style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, wordBreak: "break-word" }}
        >
          {desc}
        </div>
      </div>
      <div style={{ flexShrink: 0 }}>
        <Toggle value={enabled} onChange={onToggle} />
      </div>
    </div>
  );
}

function PaymentMethodsSection({
  gateways,
  onChange,
  error,
  cryptoError,
  gatewayConfig = {},
  availableMethodIds = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11],
  methodOrder = ["stripe", "paypal", "kurvpay", "netbilling", "hms", "nmi", "cardflo", "crypto"],
  onMethodOrderChange,
  cryptoSubOrder = ["brandWallet", "externalWallet", "guestCheckout"],
  onCryptoSubOrderChange,
}) {
  const c = gateways.crypto;
  const setCrypto = (updates) =>
    onChange({ ...gateways, crypto: { ...c, ...updates } });
  const setExtWallet = (updates) =>
    setCrypto({ externalWallet: { ...c.externalWallet, ...updates } });
  const noSubEnabled =
    c.enabled &&
    !c.brandWallet?.enabled &&
    !c.externalWallet?.enabled &&
    !c.guestCheckout;

  const showStripe = availableMethodIds.includes(1);
  const showPayPal = availableMethodIds.includes(2);
  const showBrandWallet = availableMethodIds.includes(3);
  const showExternalWallet = availableMethodIds.includes(4);
  const showGuestCheckout = availableMethodIds.includes(5);
  const showKurvPay = availableMethodIds.includes(7);
  const showHMS = availableMethodIds.includes(9);
  const showNMI = availableMethodIds.includes(11);
  const showCardFlo = availableMethodIds.includes(10);
  const showCrypto = showBrandWallet || showExternalWallet || showGuestCheckout;

  const methodDragRef = useRef(null);
  const methodDragOverRef = useRef(null);
  const [methodDragOver, setMethodDragOver] = useState(null);
  const cryptoDragRef = useRef(null);
  const cryptoDragOverRef = useRef(null);
  const [cryptoDragOver, setCryptoDragOver] = useState(null);

  const reorder = (arr, fromIdx, toIdx) => {
    const copy = [...arr];
    const [item] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, item);
    return copy;
  };

  const handleCryptoToggle = (v) => {
    if (!v) {
      setCrypto({
        enabled: false,
        brandWallet: {
          enabled: false,
          buyCryptoEnabled: false,
        },
        externalWallet: {
          enabled: false,
          providers: c.externalWallet?.providers || [],
        },
        guestCheckout: false,
      });
    } else {
      const hasSub =
        c.brandWallet?.enabled || c.externalWallet?.enabled || c.guestCheckout;
      if (hasSub) {
        setCrypto({ enabled: true });
      } else if (showBrandWallet) {
        setCrypto({ enabled: true, brandWallet: { enabled: true, buyCryptoEnabled: false } });
      } else if (showExternalWallet) {
        setCrypto({
          enabled: true,
          externalWallet: {
            enabled: true,
            providers: c.externalWallet?.providers || [
              "metamask",
              "tronlink",
              "trustwallet",
            ],
          },
        });
      } else if (showGuestCheckout) {
        setCrypto({ enabled: true, guestCheckout: true });
      } else {
        setCrypto({ enabled: true });
      }
    }
  };

  // ── Row definitions ──
  const stripeRow = showStripe ? (
    <MethodRow
      icon="mdi-credit-card"
      color="#635bff"
      label="Pay with Card"
      sublabel="Powered by PayBito Apex"
      badge="PayBito Apex"
      enabled={gateways.stripe.enabled}
      onToggle={(v) => {
        if (v && !gatewayConfig.stripe) {
          showToast(
            "error",
            "Please configure your PayBito Apex credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, stripe: { enabled: v } });
      }}
    />
  ) : null;

  const paypalRow = showPayPal ? (
    <MethodRow
      customIcon={<TitanIcon size={20} />}
      color="#003087"
      label="PayBito Titan"
      sublabel="Accept PayBito Titan payments"
      badge="PayBito Titan"
      enabled={gateways.paypal.enabled}
      onToggle={(v) => {
        if (v && !gatewayConfig.paypal) {
          showToast(
            "error",
            "Please configure your PayBito Titan credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, paypal: { enabled: v } });
      }}
    />
  ) : null;

  const kurvpayRow = showKurvPay ? (
    <MethodRow
      icon="mdi-flash"
      color="#0d9488"
      label="PayBito Nexus"
      sublabel="Accept card payments via PayBito Nexus (Collect.js)"
      badge="PayBito Nexus"
      enabled={gateways.kurvpay?.enabled || false}
      onToggle={(v) => {
        if (v && !gatewayConfig.kurvpay) {
          showToast(
            "error",
            "Please configure your PayBito Nexus credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, kurvpay: { enabled: v } });
      }}
    />
  ) : null;

  const hmsRow = showHMS ? (
    <MethodRow
      icon="mdi-bank-outline"
      color="#6366f1"
      label="PayBito Nova"
      sublabel="Accept card payments via PayBito Nova (Collect.js)"
      badge="PayBito Nova"
      enabled={gateways.hms?.enabled || false}
      onToggle={(v) => {
        if (v && !gatewayConfig.hms) {
          showToast(
            "error",
            "Please configure your PayBito Nova credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, hms: { enabled: v } });
      }}
    />
  ) : null;

  const nmiRow = showNMI ? (
    <MethodRow
      icon="mdi-flash"
      color="#7c3aed"
      label="PayBito Zenith"
      sublabel="Accept card payments via PayBito Zenith (Collect.js)"
      badge="PayBito Zenith"
      enabled={gateways.nmi?.enabled || false}
      onToggle={(v) => {
        if (v && !gatewayConfig.nmi) {
          showToast(
            "error",
            "Please configure your PayBito Zenith credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, nmi: { enabled: v } });
      }}
    />
  ) : null;

  const netbillingRow = availableMethodIds.includes(8) ? (
    <MethodRow
      icon="mdi-credit-card"
      customIcon={<VertexIcon size={20} />}
      color="#0ea5e9"
      label="PayBito Vertex"
      sublabel="Accept card payments via PayBito Vertex"
      badge="PayBito Vertex"
      enabled={gateways.netbilling?.enabled || false}
      onToggle={(v) => {
        if (v && !gatewayConfig.netbilling) {
          showToast(
            "error",
            "Please configure your PayBito Vertex credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, netbilling: { enabled: v } });
      }}
    />
  ) : null;

  const cardfloRow = showCardFlo ? (
    <MethodRow
      icon="mdi-credit-card"
      color="#4f46e5"
      label="PayBito Sovereign"
      sublabel="Accept card payments via PayBito Sovereign"
      badge="PayBito Sovereign"
      enabled={gateways.cardflo?.enabled || false}
      onToggle={(v) => {
        if (v && !gatewayConfig.cardflo) {
          showToast(
            "error",
            "Please configure your PayBito Sovereign credentials in Payment Settings before enabling."
          );
          return;
        }
        onChange({ ...gateways, cardflo: { enabled: v } });
      }}
    />
  ) : null;

  const brandWalletRow = showBrandWallet ? (
    <div
      style={{
        borderRadius: "10px",
        border: c.brandWallet?.enabled
          ? "1.5px solid color-mix(in srgb, var(--btn-color) 30%, transparent)"
          : "1.5px solid var(--body-color-less2)",
        background: c.brandWallet?.enabled
          ? "color-mix(in srgb, var(--btn-color) 6%, var(--body-color))"
          : "var(--body-color)",
        transition: "all 0.15s",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 14px",
        }}
      >
        <div
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "8px",
            background: "color-mix(in srgb, var(--btn-color) 12%, transparent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--btn-color)",
            fontSize: "15px",
            flexShrink: 0,
          }}
        >
          <i className="mdi mdi-wallet" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-color)" }}>
            Brand Wallet
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, wordBreak: "break-word" }}>
            Platform's built-in custodial crypto wallet
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Toggle
            value={c.brandWallet?.enabled || false}
            onChange={(v) => setCrypto({ brandWallet: { ...c.brandWallet, enabled: v } })}
          />
        </div>
      </div>
      {c.brandWallet?.enabled && (
        <div
          style={{
            borderTop: "1px solid color-mix(in srgb, var(--btn-color) 15%, var(--body-color-less2))",
            padding: "10px 14px 10px 44px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <input
            type="checkbox"
            checked={c.brandWallet?.buyCryptoEnabled || false}
            onChange={(e) => setCrypto({ brandWallet: { ...c.brandWallet, buyCryptoEnabled: e.target.checked } })}
            style={{ width: "14px", height: "14px", cursor: "pointer", flexShrink: 0, margin: 0 }}
          />
          <label
            style={{ fontSize: "12px", fontWeight: "500", color: "var(--text-color)", cursor: "pointer", opacity: 0.7, margin: 0, lineHeight: "1" }}
            onClick={() => setCrypto({ brandWallet: { ...c.brandWallet, buyCryptoEnabled: !c.brandWallet?.buyCryptoEnabled } })}
          >
            Enable Crypto Buy
          </label>
        </div>
      )}
    </div>
  ) : null;

  const externalWalletRow = showExternalWallet ? (
    <div
      style={{
        borderRadius: "10px",
        border: c.externalWallet?.enabled
          ? "1.5px solid color-mix(in srgb, #8b5cf6 30%, transparent)"
          : "1.5px solid var(--body-color-less2)",
        background: c.externalWallet?.enabled
          ? "color-mix(in srgb, #8b5cf6 5%, var(--body-color))"
          : "var(--body-color)",
        transition: "all 0.15s",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "12px 14px",
        }}
      >
        <div
          style={{
            width: "30px",
            height: "30px",
            borderRadius: "8px",
            background: "color-mix(in srgb, #8b5cf6 12%, transparent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#8b5cf6",
            fontSize: "15px",
            flexShrink: 0,
          }}
        >
          <i className="mdi mdi-wallet" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--text-color)" }}>
            External Wallet
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, wordBreak: "break-word" }}>
            MetaMask · TronLink · TrustWallet
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <Toggle
            value={c.externalWallet?.enabled || false}
            onChange={(v) => setExtWallet({ enabled: v })}
          />
        </div>
      </div>
      {c.externalWallet?.enabled && (
        <div
          style={{
            borderTop: "1px solid color-mix(in srgb, #8b5cf6 15%, var(--body-color-less2))",
            padding: "10px 14px 14px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          {EXTERNAL_WALLET_PROVIDERS.map((p) => (
            <div
              key={p.id}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "6px 14px",
                borderRadius: "20px",
                border: `1.5px solid color-mix(in srgb, ${p.color} 35%, transparent)`,
                background: `color-mix(in srgb, ${p.color} 8%, transparent)`,
              }}
            >
              <i className={`mdi ${p.icon}`} style={{ fontSize: "13px", color: p.color }} />
              <span style={{ fontSize: "12px", fontWeight: "700", color: p.color }}>{p.label}</span>
              <span style={{ ...S.badge, background: `color-mix(in srgb, ${p.color} 15%, transparent)`, color: p.color }}>{p.badge}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  const guestCheckoutRow = showGuestCheckout ? (
    <SubMethodRow
      icon="mdi-account"
      color="#64748b"
      label="Guest Checkout"
      desc="No wallet required — manual address copy/paste"
      enabled={c.guestCheckout}
      onToggle={(v) => setCrypto({ guestCheckout: v })}
    />
  ) : null;

  const cryptoSubMap = {
    brandWallet: brandWalletRow,
    externalWallet: externalWalletRow,
    guestCheckout: guestCheckoutRow,
  };

  // ── Crypto section (with inner drag-to-reorder for sub-options) ──
  const cryptoSection = showCrypto ? (
    <div
      style={{
        borderRadius: "14px",
        border: c.enabled
          ? "1.5px solid color-mix(in srgb, #f59e0b 35%, transparent)"
          : "1.5px solid var(--body-color-less2)",
        background: c.enabled
          ? "color-mix(in srgb, #f59e0b 4%, var(--body-color-less))"
          : "var(--body-color-less)",
        transition: "all 0.15s",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "16px 20px" }}>
        <div
          style={{
            width: "38px",
            height: "38px",
            borderRadius: "11px",
            background: "color-mix(in srgb, #f59e0b 15%, transparent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#f59e0b",
            fontSize: "18px",
            flexShrink: 0,
          }}
        >
          <i className="mdi mdi-bitcoin" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px 8px" }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: "var(--text-color)" }}>
              Pay with Crypto
            </span>
            <span style={{ ...S.badge, background: "color-mix(in srgb, #f59e0b 15%, transparent)", color: "#f59e0b" }}>
              PayBito
            </span>
          </div>
          <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px", wordBreak: "break-word" }}>
            Brand wallet, external wallet, or guest checkout
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <span style={{ fontSize: "11px", fontWeight: "600", color: "var(--text-color)", opacity: c.enabled ? 0.8 : 0.4 }}>
            {c.enabled ? "Enabled" : "Disabled"}
          </span>
          <Toggle value={c.enabled} onChange={handleCryptoToggle} />
        </div>
      </div>

      {c.enabled && (
        <div
          style={{
            borderTop: "1px solid color-mix(in srgb, #f59e0b 20%, var(--body-color-less2))",
            padding: "16px 20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            background: "color-mix(in srgb, var(--body-color) 70%, transparent)",
          }}
        >
          {noSubEnabled && (
            <div style={{ fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "5px" }}>
              <i className="mdi mdi-alert-circle" style={{ fontSize: "13px" }} />
              At least one checkout method must be enabled under Crypto
            </div>
          )}

          {/* Crypto sub-options — draggable within this section only */}
          {cryptoSubOrder.map((key, index) => {
            const row = cryptoSubMap[key];
            if (!row) return null;
            const isCryptoDragTarget =
              cryptoDragOver === index &&
              cryptoDragRef.current !== null &&
              cryptoDragRef.current !== index;
            return (
              <div
                key={key}
                draggable
                onDragStart={(e) => {
                  e.stopPropagation();
                  cryptoDragRef.current = index;
                  setCryptoDragOver(null);
                }}
                onDragEnter={(e) => {
                  e.stopPropagation();
                  cryptoDragOverRef.current = index;
                  setCryptoDragOver(index);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragLeave={(e) => {
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnd={(e) => {
                  e.stopPropagation();
                  if (
                    cryptoDragRef.current !== null &&
                    cryptoDragOverRef.current !== null &&
                    cryptoDragRef.current !== cryptoDragOverRef.current
                  ) {
                    onCryptoSubOrderChange(
                      reorder(cryptoSubOrder, cryptoDragRef.current, cryptoDragOverRef.current)
                    );
                  }
                  cryptoDragRef.current = null;
                  cryptoDragOverRef.current = null;
                  setCryptoDragOver(null);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  opacity: isCryptoDragTarget ? 0.5 : 1,
                  transition: "opacity 0.15s",
                  borderRadius: "10px",
                  outline: isCryptoDragTarget
                    ? "2px dashed color-mix(in srgb, #f59e0b 55%, transparent)"
                    : "2px solid transparent",
                }}
              >
                <div
                  title="Drag to reorder"
                  style={{
                    cursor: "grab",
                    padding: "0 4px",
                    opacity: 0.6,
                    color: "var(--text-color)",
                    userSelect: "none",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                  }}
                >
                  <i className="mdi mdi-drag-vertical" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>{row}</div>
              </div>
            );
          })}

          <div style={{ paddingTop: "6px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "8px",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  color: "var(--text-color)",
                  opacity: 0.65,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="mdi mdi-currency-btc" style={{ color: "#f59e0b", fontSize: "13px" }} />{" "}
                Cryptos Available at Checkout
              </div>
              <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => setCrypto({ cryptos: [...ALL_PAYBITO_CODES] })}
                  style={{
                    padding: "3px 10px",
                    borderRadius: "6px",
                    border: "1px solid var(--body-color-less2)",
                    background: "transparent",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "var(--btn-color)",
                    cursor: "pointer",
                  }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => setCrypto({ cryptos: [] })}
                  style={{
                    padding: "3px 10px",
                    borderRadius: "6px",
                    border: "1px solid var(--body-color-less2)",
                    background: "transparent",
                    fontSize: "11px",
                    fontWeight: "600",
                    color: "var(--text-color)",
                    opacity: 0.6,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {PAYBITO_CRYPTOS.map((cr) => {
                const checked = (c.cryptos || ALL_PAYBITO_CODES).includes(cr.code);
                return (
                  <label
                    key={cr.code}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                      padding: "7px 14px",
                      borderRadius: "20px",
                      cursor: "pointer",
                      transition: "all 0.12s",
                      border: checked ? "1.5px solid #f59e0b" : "1.5px solid var(--body-color-less2)",
                      background: checked ? "color-mix(in srgb, #f59e0b 10%, transparent)" : "var(--body-color)",
                      userSelect: "none",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const cur = c.cryptos || ALL_PAYBITO_CODES;
                        setCrypto({
                          cryptos: e.target.checked
                            ? [...cur, cr.code]
                            : cur.filter((x) => x !== cr.code),
                        });
                      }}
                      style={{ width: "13px", height: "13px", accentColor: "#f59e0b", cursor: "pointer" }}
                    />
                    <span style={{ fontSize: "12px", fontWeight: "700", color: checked ? "#f59e0b" : "var(--text-color)", opacity: checked ? 1 : 0.6 }}>
                      {cr.code}
                    </span>
                    <span style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.45 }}>
                      {cr.name}
                    </span>
                  </label>
                );
              })}
            </div>
            {(c.cryptos || ALL_PAYBITO_CODES).length === 0 && (
              <div style={{ marginTop: "8px", fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "5px" }}>
                <i className="mdi mdi-alert-circle" style={{ fontSize: "13px" }} />{" "}
                At least one cryptocurrency must be selected
              </div>
            )}
            {cryptoError && (
              <div style={{ marginTop: "6px", fontSize: "11px", color: "#ef4444", display: "flex", alignItems: "center", gap: "5px" }}>
                <i className="mdi mdi-alert-circle" style={{ fontSize: "13px" }} />
                {cryptoError}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  ) : null;

  const methodMap = {
    stripe: stripeRow,
    paypal: paypalRow,
    kurvpay: kurvpayRow,
    netbilling: netbillingRow,
    hms: hmsRow,
    nmi: nmiRow,
    cardflo: cardfloRow,
    crypto: cryptoSection,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {error && (
        <div
          style={{
            fontSize: "11px",
            color: "#ef4444",
            display: "flex",
            alignItems: "center",
            gap: "5px",
            marginBottom: "2px",
          }}
        >
          <i className="mdi mdi-alert-circle" style={{ fontSize: "13px" }} />
          {error}
        </div>
      )}

      {/* Top-level payment methods — draggable to reorder */}
      {methodOrder.map((key, index) => {
        const row = methodMap[key];
        if (!row) return null;
        const isMethodDragTarget =
          methodDragOver === index &&
          methodDragRef.current !== null &&
          methodDragRef.current !== index;
        return (
          <div
            key={key}
            draggable
            onDragStart={() => {
              methodDragRef.current = index;
              setMethodDragOver(null);
            }}
            onDragEnter={() => {
              methodDragOverRef.current = index;
              setMethodDragOver(index);
            }}
            onDragOver={(e) => {
              e.preventDefault();
            }}
            onDragLeave={() => { }}
            onDrop={(e) => {
              e.preventDefault();
            }}
            onDragEnd={() => {
              if (
                methodDragRef.current !== null &&
                methodDragOverRef.current !== null &&
                methodDragRef.current !== methodDragOverRef.current
              ) {
                onMethodOrderChange(
                  reorder(methodOrder, methodDragRef.current, methodDragOverRef.current)
                );
              }
              methodDragRef.current = null;
              methodDragOverRef.current = null;
              setMethodDragOver(null);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              opacity: isMethodDragTarget ? 0.5 : 1,
              transition: "opacity 0.15s",
              borderRadius: "14px",
              outline: isMethodDragTarget
                ? "2px dashed color-mix(in srgb, var(--btn-color) 55%, transparent)"
                : "2px solid transparent",
            }}
          >
            <div
              title="Drag to reorder"
              style={{
                cursor: "grab",
                padding: "0 4px",
                opacity: 0.6,
                color: "var(--text-color)",
                userSelect: "none",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                fontSize: "18px",
              }}
            >
              <i className="mdi mdi-drag-vertical" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{row}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SINGLE PAYMENT — form
// ─────────────────────────────────────────────────────────────────
function SinglePaymentForm({
  initial,
  onSave,
  onCancel,
  saving,
  gatewayConfig = {},
  availableMethodIds = [1, 2, 3, 4, 5, 7, 8, 9, 10, 11],
}) {
  const makeDefaultGateways = () => ({
    stripe: { enabled: false },
    paypal: { enabled: false },
    kurvpay: { enabled: false },
    netbilling: { enabled: false },
    hms: { enabled: false },
    nmi: { enabled: false },
    cardflo: { enabled: false },
    crypto: {
      enabled: true,
      brandWallet: {
        enabled: availableMethodIds.includes(3),
        buyCryptoEnabled: true,
      },
      externalWallet: {
        enabled: false,
        providers: ["metamask", "tronlink", "trustwallet"],
      },
      guestCheckout: false,
      cryptos: [...ALL_PAYBITO_CODES],
    },
  });

  const makeDefaultMethodOrder = () => {
    const order = [];
    if (availableMethodIds.includes(1)) order.push("stripe");
    if (availableMethodIds.includes(2)) order.push("paypal");
    if (availableMethodIds.includes(7)) order.push("kurvpay");
    if (availableMethodIds.includes(8)) order.push("netbilling");
    if (availableMethodIds.includes(9)) order.push("hms");
    if (availableMethodIds.includes(11)) order.push("nmi");
    if (availableMethodIds.includes(10)) order.push("cardflo");
    if (availableMethodIds.includes(3) || availableMethodIds.includes(4) || availableMethodIds.includes(5))
      order.push("crypto");
    return order;
  };

  const makeDefaultCryptoSubOrder = () => {
    const order = [];
    if (availableMethodIds.includes(3)) order.push("brandWallet");
    if (availableMethodIds.includes(4)) order.push("externalWallet");
    if (availableMethodIds.includes(5)) order.push("guestCheckout");
    return order;
  };

  const [form, setForm] = useState(() =>
    initial
      ? {
        ...initial,
        gateways: JSON.parse(JSON.stringify(initial.gateways)),
        methodOrder: initial.methodOrder || makeDefaultMethodOrder(),
        cryptoSubOrder: initial.cryptoSubOrder || makeDefaultCryptoSubOrder(),
      }
      : {
        id: null,
        name: "",
        customerEmail: "",
        gateways: makeDefaultGateways(),
        processingFeeHandling: "customer",
        redirectUrl: "",
        isRecurring: false,
        recurringEmail: "",
        recurringPeriod: "monthly",
        totalCycles: "",
        noEndDate: false,
        isDefault: false,
        methodOrder: makeDefaultMethodOrder(),
        cryptoSubOrder: makeDefaultCryptoSubOrder(),
      }
  );
  const [errors, setErrors] = useState({});
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const merge = (obj) => setForm((f) => ({ ...f, ...obj }));

  const validate = () => {
    const e = {};
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      e.name = "Profile name is required";
    } else if (trimmedName.length < 2 || trimmedName.length > 100) {
      e.name = "Profile name must be 2–100 characters";
    } else if (!/^[a-zA-Z0-9 \-&]+$/.test(trimmedName)) {
      e.name = "Only letters, numbers, spaces, hyphens, and & are allowed";
    }
    if (form.customerEmail && form.customerEmail.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail.trim()))
        e.customerEmail = "Enter a valid email address";
    }
    const g = form.gateways;
    if (!g.stripe?.enabled && !g.paypal?.enabled && !g.kurvpay?.enabled && !g.hms?.enabled && !g.nmi?.enabled && !g.netbilling?.enabled && !g.cardflo?.enabled && !g.crypto?.enabled)
      e.gateways = "At least one payment method must be enabled";
    if (g.crypto.enabled) {
      if (
        !g.crypto.brandWallet?.enabled &&
        !g.crypto.externalWallet?.enabled &&
        !g.crypto.guestCheckout
      )
        e.gateways =
          "If crypto is enabled, at least one checkout method must be enabled";
      if ((g.crypto.cryptos || ALL_PAYBITO_CODES).length === 0)
        e.cryptos = "At least one cryptocurrency must be selected";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <div style={S.formGroup}>
        <label style={S.label}>Profile Name *</label>
        <input
          style={errors.name ? S.inputError : S.input}
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Standard Crypto Checkout"
        />
        {errors.name && <span style={S.errorText}>{errors.name}</span>}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "14px 18px",
          borderRadius: "12px",
          border: "1px solid var(--body-color-less2)",
          background: "var(--body-color)",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "700",
              color: "var(--text-color)",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i
              className="mdi mdi-star"
              style={{ color: "#f59e0b" }}
            />
            Set as Default
          </span>
          <span
            style={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.6,
            }}
          >
            Use this profile as the default billing profile.
          </span>
        </div>
        <Toggle
          value={!!form.isDefault}
          onChange={(v) => set("isDefault", v)}
        />
      </div>
      {/* <div style={S.formGroup}>
        <label style={S.label}>Customer Email</label>
        <div style={{ position: "relative" }}>
          <i
            className="mdi mdi-email"
            style={{
              position: "absolute",
              left: "13px",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "16px",
              color: "var(--text-color)",
              opacity: 0.35,
              pointerEvents: "none",
            }}
          />
          <input
            type="email"
            style={{ ...S.input, paddingLeft: "40px", ...(errors.customerEmail ? { borderColor: "#ef4444" } : {}) }}
            value={form.customerEmail || ""}
            onChange={(e) => set("customerEmail", e.target.value)}
            placeholder="customer@example.com"
            autoComplete="off"
          />
        </div>
        {errors.customerEmail && <span style={S.errorText}>{errors.customerEmail}</span>}
      </div> */}
      <div>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "700",
            color: "var(--text-color)",
            marginBottom: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i
            className="mdi mdi-transit-connection-variant"
            style={{ color: "var(--btn-color)", fontSize: "15px" }}
          />
          Payment Methods
          <span
            style={{
              fontSize: "11px",
              color: "var(--text-color)",
              opacity: 0.45,
              marginLeft: "2px",
            }}
          >
            — Enable options available to customers at checkout
          </span>
        </div>
        <PaymentMethodsSection
          gateways={form.gateways}
          onChange={(gw) => set("gateways", gw)}
          error={errors.gateways}
          cryptoError={errors.cryptos}
          gatewayConfig={gatewayConfig}
          availableMethodIds={availableMethodIds}
          methodOrder={form.methodOrder}
          onMethodOrderChange={(order) => set("methodOrder", order)}
          cryptoSubOrder={form.cryptoSubOrder}
          onCryptoSubOrderChange={(order) => set("cryptoSubOrder", order)}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          paddingTop: "4px",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={S.btnOutline}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (validate()) onSave(form);
          }}
          style={{
            ...S.btnPrimary,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="mdi mdi-loading mdi-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="mdi mdi-check" /> Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SINGLE PAYMENT — list card
// ─────────────────────────────────────────────────────────────────
function SinglePaymentCard({
  profile,
  onEdit,
  onDelete,
  onMakeDefault,
  isDeleting,
  isMakingDefault,
  isReadOnly,
  isTestMode,
}) {
  const g = profile.gateways;
  const c = g?.crypto;
  return (
    <div
      style={{
        ...S.card,
        opacity: isDeleting ? 0.6 : 1,
        transition: "opacity 0.2s",
        border: profile.isDefault
          ? "1.5px solid color-mix(in srgb, var(--btn-color) 40%, transparent)"
          : undefined,
      }}
    >
      <div style={S.cardHeader}>
        <div
          style={{
            ...S.cardHeaderIcon,
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 15%, transparent), color-mix(in srgb, var(--btn-color) 5%, transparent))",
            color: "var(--btn-color)",
          }}
        >
          <i className="mdi mdi-credit-card" />
        </div>
        <h4 style={S.cardHeaderTitle}>{profile.name}</h4>
        {profile.isDefault && (
          <span
            style={{
              padding: "3px 10px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "700",
              background:
                "color-mix(in srgb, var(--btn-color) 12%, transparent)",
              color: "var(--btn-color)",
              marginLeft: "8px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <i className="mdi mdi-check-circle" style={{ fontSize: "12px" }} />{" "}
            Default
          </span>
        )}
        {!isReadOnly && (
          <div style={{ flex: "1 0 100%", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {!profile.isDefault && (
              <button
                onClick={onMakeDefault}
                disabled={isDeleting || isMakingDefault}
                style={{
                  padding: "8px 14px",
                  background:
                    "color-mix(in srgb, var(--btn-color) 8%, transparent)",
                  border:
                    "1px solid color-mix(in srgb, var(--btn-color) 25%, transparent)",
                  color: "var(--btn-color)",
                  borderRadius: "10px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor:
                    isDeleting || isMakingDefault ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: isDeleting || isMakingDefault ? 0.5 : 1,
                  transition: "all 0.15s",
                  whiteSpace: "nowrap",
                }}
              >
                {isMakingDefault ? (
                  <i
                    className="mdi mdi-loading mdi-spin"
                    style={{ fontSize: "13px" }}
                  />
                ) : (
                  <i
                    className="mdi mdi-star"
                    style={{ fontSize: "13px" }}
                  />
                )}
                Make Default
              </button>
            )}
            <button onClick={onEdit} style={S.btnOutline} disabled={isDeleting}>
              <i className="mdi mdi-pencil" /> Edit
            </button>
            <button
              onClick={onDelete}
              style={{
                ...S.btnDanger,
                opacity: isDeleting ? 0.5 : 1,
                cursor: isDeleting ? "not-allowed" : "pointer",
              }}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <i
                  className="mdi mdi-loading mdi-spin"
                  style={{ fontSize: "14px" }}
                />
              ) : (
                <i className="mdi mdi-delete" />
              )}
            </button>
          </div>
        )}
      </div>
      <div style={S.cardBody}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "20px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                color: "var(--text-color)",
                opacity: 0.5,
                marginBottom: "8px",
              }}
            >
              Payment Methods
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
              {g?.stripe?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #635bff 12%, transparent)",
                    color: "#635bff",
                    border:
                      "1px solid color-mix(in srgb, #635bff 25%, transparent)",
                  }}
                >
                  <i
                    className="mdi mdi-credit-card"
                    style={{ fontSize: "12px" }}
                  />{" "}
                  Pay with Card
                </span>
              )}
              {g?.paypal?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #003087 12%, transparent)",
                    color: "#003087",
                    border:
                      "1px solid color-mix(in srgb, #003087 25%, transparent)",
                  }}
                >
                  <TitanIcon size={12} />{" "}
                  PayBito Titan
                </span>
              )}
              {g?.kurvpay?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #0d9488 12%, transparent)",
                    color: "#0d9488",
                    border: "1px solid color-mix(in srgb, #0d9488 25%, transparent)",
                  }}
                >
                  <i className="mdi mdi-flash" style={{ fontSize: "12px" }} />{" "}
                  PayBito Nexus
                </span>
              )}
              {g?.netbilling?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #0ea5e9 12%, transparent)",
                    color: "#0ea5e9",
                    border: "1px solid color-mix(in srgb, #0ea5e9 25%, transparent)",
                  }}
                >
                  <VertexIcon size={12} />{" "}
                  PayBito Vertex
                </span>
              )}
              {g?.hms?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #6366f1 12%, transparent)",
                    color: "#6366f1",
                    border: "1px solid color-mix(in srgb, #6366f1 25%, transparent)",
                  }}
                >
                  <i className="mdi mdi-bank-outline" style={{ fontSize: "12px" }} />{" "}
                  PayBito Nova
                </span>
              )}
              {g?.nmi?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #7c3aed 12%, transparent)",
                    color: "#7c3aed",
                    border: "1px solid color-mix(in srgb, #7c3aed 25%, transparent)",
                  }}
                >
                  <i className="mdi mdi-flash" style={{ fontSize: "12px" }} />{" "}
                  PayBito Zenith
                </span>
              )}
              {g?.cardflo?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #4f46e5 12%, transparent)",
                    color: "#4f46e5",
                    border: "1px solid color-mix(in srgb, #4f46e5 25%, transparent)",
                  }}
                >
                  <i className="mdi mdi-credit-card" style={{ fontSize: "12px" }} />{" "}
                  PayBito Sovereign
                </span>
              )}
              {c?.enabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "4px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: "color-mix(in srgb, #f59e0b 12%, transparent)",
                    color: "#f59e0b",
                    border:
                      "1px solid color-mix(in srgb, #f59e0b 25%, transparent)",
                  }}
                >
                  <i className="mdi mdi-bitcoin" style={{ fontSize: "12px" }} />{" "}
                  Crypto
                </span>
              )}
              {!g?.stripe?.enabled && !g?.paypal?.enabled && !g?.kurvpay?.enabled && !g?.netbilling?.enabled && !g?.hms?.enabled && !g?.nmi?.enabled && !g?.cardflo?.enabled && !c?.enabled && (
                <span
                  style={{
                    fontSize: "12px",
                    color: "var(--text-color)",
                    opacity: 0.35,
                  }}
                >
                  None enabled
                </span>
              )}
            </div>
            {c?.enabled && (
              <div
                style={{
                  marginTop: "10px",
                  paddingLeft: "4px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                }}
              >
                {c.brandWallet?.enabled && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-color)",
                      opacity: 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <i
                      className="mdi mdi-wallet"
                      style={{ fontSize: "12px", color: "var(--btn-color)" }}
                    />{" "}
                    Brand Wallet
                  </div>
                )}
                {c.externalWallet?.enabled && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-color)",
                      opacity: 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <i
                      className="mdi mdi-wallet"
                      style={{ fontSize: "12px", color: "#8b5cf6" }}
                    />{" "}
                    External Wallet (
                    {(c.externalWallet.providers || []).join(", ") || "none"})
                  </div>
                )}
                {c.guestCheckout && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "var(--text-color)",
                      opacity: 0.6,
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <i
                      className="mdi mdi-account"
                      style={{ fontSize: "12px", color: "#64748b" }}
                    />{" "}
                    Guest Checkout
                  </div>
                )}
                <div
                  style={{
                    marginTop: "4px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                  }}
                >
                  {(c.cryptos || ALL_PAYBITO_CODES).map((code) => (
                    <span
                      key={code}
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: "700",
                        background: "rgba(245,158,11,0.1)",
                        color: "#f59e0b",
                      }}
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SUBSCRIPTION — form
// ─────────────────────────────────────────────────────────────────
function SubscriptionForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(
    initial || {
      id: null,
      name: "",
      availableCryptos: [...ALL_PAYBITO_CODES],
      processingFeeHandling: "customer",
      deliveryMode: "request",
      customerEmail: "",
      recurringPeriod: "monthly",
      billingStartDate: "",
      totalCycles: "",
      noEndDate: false,
      retryAttempts: "3",
      redirectUrl: "",
    }
  );
  const [errors, setErrors] = useState({});
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const merge = (obj) => setForm((f) => ({ ...f, ...obj }));

  const toggleCrypto = (code) => {
    if (form.availableCryptos.includes(code)) {
      if (form.availableCryptos.length === 1) return;
      set(
        "availableCryptos",
        form.availableCryptos.filter((c) => c !== code)
      );
    } else {
      set("availableCryptos", [...form.availableCryptos, code]);
    }
  };

  const validate = () => {
    const e = {};
    const trimmedName = form.name.trim();
    if (!trimmedName) {
      e.name = "Profile name is required";
    } else if (trimmedName.length < 2 || trimmedName.length > 100) {
      e.name = "Profile name must be 2–100 characters";
    } else if (!/^[a-zA-Z0-9 \-&]+$/.test(trimmedName)) {
      e.name = "Only letters, numbers, spaces, hyphens, and & are allowed";
    }
    if (form.availableCryptos.length === 0)
      e.cryptos = "At least one cryptocurrency must be selected";
    if (form.deliveryMode === "request") {
      if (!form.customerEmail.trim())
        e.customerEmail =
          "Customer email is required when sending request directly";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerEmail.trim()))
        e.customerEmail = "Enter a valid email address";
    }
    if (
      form.redirectUrl.trim() &&
      !/^https?:\/\/.+/.test(form.redirectUrl.trim())
    )
      e.redirectUrl = "Enter a valid URL starting with http:// or https://";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <div style={S.formGroup}>
        <label style={S.label}>Profile Name *</label>
        <input
          style={errors.name ? S.inputError : S.input}
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          placeholder="e.g. Monthly SaaS Subscription"
        />
        {errors.name && <span style={S.errorText}>{errors.name}</span>}
      </div>
      <div style={S.formGroup}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <label style={S.label}>Cryptos Available to Customer</label>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              onClick={() => set("availableCryptos", [...ALL_PAYBITO_CODES])}
              style={{
                padding: "3px 10px",
                borderRadius: "6px",
                border: "1px solid var(--body-color-less2)",
                background: "transparent",
                fontSize: "11px",
                fontWeight: "600",
                color: "var(--btn-color)",
                cursor: "pointer",
              }}
            >
              Select All
            </button>
            <button
              type="button"
              onClick={() => set("availableCryptos", [])}
              style={{
                padding: "3px 10px",
                borderRadius: "6px",
                border: "1px solid var(--body-color-less2)",
                background: "transparent",
                fontSize: "11px",
                fontWeight: "600",
                color: "var(--text-color)",
                opacity: 0.6,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {SUB_CRYPTOS.map((cr) => {
            const checked = form.availableCryptos.includes(cr.code);
            return (
              <label
                key={cr.code}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "7px",
                  padding: "7px 14px",
                  borderRadius: "20px",
                  cursor: "pointer",
                  transition: "all 0.12s",
                  border: checked
                    ? "1.5px solid var(--btn-color)"
                    : "1.5px solid var(--body-color-less2)",
                  background: checked
                    ? "color-mix(in srgb, var(--btn-color) 10%, transparent)"
                    : "var(--body-color)",
                  userSelect: "none",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleCrypto(cr.code)}
                  style={{
                    width: "13px",
                    height: "13px",
                    accentColor: "var(--btn-color)",
                    cursor: "pointer",
                  }}
                />
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    color: checked ? "var(--btn-color)" : "var(--text-color)",
                    opacity: checked ? 1 : 0.6,
                  }}
                >
                  {cr.code}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    color: "var(--text-color)",
                    opacity: 0.45,
                  }}
                >
                  {cr.name}
                </span>
              </label>
            );
          })}
        </div>
        {errors.cryptos && <span style={S.errorText}>{errors.cryptos}</span>}
      </div>
      <ProcessingFeeField
        value={form.processingFeeHandling}
        onChange={(v) => set("processingFeeHandling", v)}
      />
      <div style={S.sectionBox}>
        <div style={S.sectionTitle}>
          <i
            className="mdi mdi-email-outline"
            style={{ color: "#8b5cf6" }}
          />{" "}
          Billing Request Delivery
        </div>
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
          {[
            {
              val: "request",
              label: "Send to Customer Email",
              icon: "mdi-email-outline",
              desc: "You provide the email — bill is sent directly",
            },
            {
              val: "form",
              label: "Customer Fills at Checkout",
              icon: "mdi-clipboard-text",
              desc: "Customer enters their email during checkout",
            },
          ].map((opt) => (
            <div
              key={opt.val}
              onClick={() => {
                set("deliveryMode", opt.val);
                if (opt.val === "form") set("customerEmail", "");
              }}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                padding: "12px 14px",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.15s",
                border:
                  form.deliveryMode === opt.val
                    ? "1.5px solid #8b5cf6"
                    : "1.5px solid var(--body-color-less2)",
                background:
                  form.deliveryMode === opt.val
                    ? "rgba(139,92,246,0.06)"
                    : "var(--body-color)",
              }}
            >
              <RadioDot active={form.deliveryMode === opt.val} />
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                    color: "var(--text-color)",
                  }}
                >
                  <i
                    className={`mdi ${opt.icon}`}
                    style={{
                      fontSize: "14px",
                      color:
                        form.deliveryMode === opt.val
                          ? "#8b5cf6"
                          : "var(--text-color)",
                      opacity: form.deliveryMode === opt.val ? 1 : 0.4,
                    }}
                  />{" "}
                  {opt.label}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-color)",
                    opacity: 0.5,
                    marginTop: "2px",
                  }}
                >
                  {opt.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>
            Customer Email
            {form.deliveryMode === "request" ? (
              <span style={{ color: "#ef4444", marginLeft: "3px" }}>*</span>
            ) : (
              <span
                style={{
                  marginLeft: "6px",
                  fontSize: "10px",
                  fontWeight: "600",
                  background: "rgba(100,116,139,0.12)",
                  color: "#64748b",
                  borderRadius: "4px",
                  padding: "1px 5px",
                }}
              >
                OPTIONAL
              </span>
            )}
          </label>
          <div style={{ position: "relative" }}>
            <i
              className="mdi mdi-email"
              style={{
                position: "absolute",
                left: "13px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "16px",
                color: "var(--text-color)",
                opacity: 0.35,
                pointerEvents: "none",
              }}
            />
            <input
              type="email"
              style={{
                ...(errors.customerEmail ? S.inputError : S.input),
                paddingLeft: "40px",
              }}
              value={form.customerEmail}
              onChange={(e) => set("customerEmail", e.target.value)}
              placeholder={
                form.deliveryMode === "request"
                  ? "customer@example.com"
                  : "Pre-fill email (optional)"
              }
              autoComplete="off"
            />
          </div>
          {errors.customerEmail && (
            <span style={S.errorText}>{errors.customerEmail}</span>
          )}
          {form.deliveryMode === "form" && (
            <span
              style={{
                fontSize: "11px",
                color: "var(--text-color)",
                opacity: 0.45,
                marginTop: "2px",
              }}
            >
              Customer enters their email at checkout — you can pre-fill it here
              as a default
            </span>
          )}
        </div>
      </div>
      <RecurringCycleBlock
        recurringPeriod={form.recurringPeriod}
        totalCycles={form.totalCycles}
        noEndDate={form.noEndDate}
        onChange={(updates) => merge(updates)}
      />
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}
      >
        <div style={S.formGroup}>
          <label style={S.label}>Billing Start Date</label>
          <input
            type="date"
            style={S.input}
            value={form.billingStartDate}
            onChange={(e) => set("billingStartDate", e.target.value)}
          />
        </div>
        <div style={S.formGroup}>
          <label style={S.label}>Retry Attempts if Payment Fails</label>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              min="0"
              max="10"
              style={{ ...S.input, paddingRight: "78px" }}
              value={form.retryAttempts}
              onChange={(e) => set("retryAttempts", e.target.value)}
              placeholder="3"
            />
            <span
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "11px",
                fontWeight: "600",
                color: "var(--text-color)",
                opacity: 0.4,
                pointerEvents: "none",
              }}
            >
              attempts
            </span>
          </div>
        </div>
      </div>
      <RedirectUrlField
        value={form.redirectUrl}
        onChange={(v) => set("redirectUrl", v)}
        error={errors.redirectUrl}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          paddingTop: "4px",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          style={S.btnOutline}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (validate()) onSave(form);
          }}
          style={{
            ...S.btnPrimary,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          disabled={saving}
        >
          {saving ? (
            <>
              <i className="mdi mdi-loading mdi-spin" /> Saving…
            </>
          ) : (
            <>
              <i className="mdi mdi-check" /> Save Profile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SUBSCRIPTION — list card
// ─────────────────────────────────────────────────────────────────
const PERIOD_LABEL = {
  weekly: "Weekly",
  biweekly: "Bi-Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
};

function SubscriptionCard({ profile, onEdit, onDelete, isDeleting }) {
  const meta = getPeriodMeta(profile.recurringPeriod);
  return (
    <div
      style={{
        ...S.card,
        opacity: isDeleting ? 0.6 : 1,
        transition: "opacity 0.2s",
      }}
    >
      <div style={S.cardHeader}>
        <div
          style={{
            ...S.cardHeaderIcon,
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.05))",
            color: "#8b5cf6",
          }}
        >
          <i className="mdi mdi-refresh" />
        </div>
        <h4 style={S.cardHeaderTitle}>{profile.name}</h4>
        <span
          style={{
            padding: "3px 10px",
            borderRadius: "20px",
            fontSize: "11px",
            fontWeight: "600",
            background: "rgba(139,92,246,0.1)",
            color: "#8b5cf6",
            marginLeft: "8px",
          }}
        >
          {PERIOD_LABEL[profile.recurringPeriod] || profile.recurringPeriod}
        </span>
        {profile.noEndDate ? (
          <span
            style={{
              padding: "3px 10px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "600",
              background: "rgba(34,197,94,0.1)",
              color: "#22c55e",
              marginLeft: "6px",
            }}
          >
            No End Date
          </span>
        ) : profile.totalCycles ? (
          <span
            style={{
              padding: "3px 10px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "600",
              background: "rgba(34,197,94,0.1)",
              color: "#22c55e",
              marginLeft: "6px",
            }}
          >
            {profile.totalCycles} {meta.unit}
          </span>
        ) : null}
        <div style={{ flex: "1 0 100%", display: "flex", flexWrap: "wrap", gap: "8px" }}>
          <button onClick={onEdit} style={S.btnOutline} disabled={isDeleting}>
            <i className="mdi mdi-pencil" /> Edit
          </button>
          <button
            onClick={onDelete}
            style={{
              ...S.btnDanger,
              opacity: isDeleting ? 0.5 : 1,
              cursor: isDeleting ? "not-allowed" : "pointer",
            }}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <i
                className="mdi mdi-loading mdi-spin"
                style={{ fontSize: "14px" }}
              />
            ) : (
              <i className="mdi mdi-delete" />
            )}
          </button>
        </div>
      </div>
      <div style={S.cardBody}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "20px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.3px",
                color: "var(--text-color)",
                opacity: 0.5,
                marginBottom: "8px",
              }}
            >
              Available Cryptos
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
              {profile.availableCryptos.map((c) => (
                <span
                  key={c}
                  style={{
                    padding: "3px 10px",
                    borderRadius: "20px",
                    fontSize: "11px",
                    fontWeight: "700",
                    background: "rgba(139,92,246,0.1)",
                    color: "#8b5cf6",
                    border: "1px solid rgba(139,92,246,0.2)",
                  }}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <MetaRow
              icon="mdi-cash"
              label="Fee"
              value={
                profile.processingFeeHandling === "customer"
                  ? "Customer Pays"
                  : "Merchant Absorbs"
              }
            />
            <MetaRow
              icon="mdi-email-outline"
              label="Delivery"
              value={
                profile.deliveryMode === "request"
                  ? "Send to Customer"
                  : "Form (customer fills)"
              }
              color="#8b5cf6"
            />
            {profile.customerEmail && (
              <MetaRow
                icon="mdi-email"
                label="Email"
                value={profile.customerEmail}
              />
            )}
            {profile.billingStartDate && (
              <MetaRow
                icon="mdi-calendar-today"
                label="Starts"
                value={profile.billingStartDate}
              />
            )}
            <MetaRow
              icon="mdi-alert-circle"
              label="Retry on fail"
              value={`${profile.retryAttempts || "3"} attempts`}
            />
            {profile.redirectUrl && (
              <MetaRow
                icon="mdi-link-variant"
                label="Redirect"
                value={profile.redirectUrl}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ROOT component
// ─────────────────────────────────────────────────────────────────
export default function Billing() {
  const { isTestMode } = useSelector((state) => state);
  const merchantId = getMerchantId();
  const isReadOnly = currentPageAccess() === "READ";

  const [singleProfiles, setSingleProfiles] = useState([]);
  const [singleMode, setSingleMode] = useState("list");
  const [singleEditing, setSingleEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [makingDefaultId, setMakingDefaultId] = useState(null);
  const [cryptoMaps, setCryptoMaps] = useState({ codeToId: {}, idToCode: {} });
  const [gatewayConfig, setGatewayConfig] = useState({
    stripe: false,
    paypal: false,
    kurvpay: false,
    netbilling: false,
    hms: false,
    nmi: false,
    cardflo: false,
  });
  const [availableMethodIds, setAvailableMethodIds] = useState([1, 2, 3, 4, 5, 8, 10]);

  // ── Load billing profiles ──
  const loadProfiles = (maps) => {
    setLoading(true);
    api
      .fetchAll(merchantId)
      .then((res) => {
        if (res.error !== 0) {
          showToast("error", res.message || "Failed to load billing profiles.");
          return;
        }
        const records = res.data || [];
        const resolvedMaps = maps || cryptoMaps;
        setSingleProfiles(
          records
            .filter((r) => r.billingType === "SINGLE")
            .map((r) => mapRecordToSingle(r, resolvedMaps.idToCode))
        );
      })
      .catch((err) => showHttpError(err))
      .finally(() => setLoading(false));
  };

  // ── Fetch currencies + gateways on mount ──
  useEffect(() => {
    // Fetch currencies
    api
      .fetchCurrencies(merchantId)
      .then((res) => {
        const arr = Array.isArray(res) ? res[0] : res;
        const allCurrencies =
          arr?.rolling_reserve_balance || arr?.coin_balance || [];
        const currencies = allCurrencies.filter(
          (c) => String(c.currency_type) === "2"
        );

        PAYBITO_CRYPTOS = currencies.map((c) => ({
          code: c.currency_code,
          name: c.currency_name.trim(),
          logo: c.logo,
        }));
        ALL_PAYBITO_CODES = PAYBITO_CRYPTOS.map((c) => c.code);
        SUB_CRYPTOS = [...PAYBITO_CRYPTOS];

        const maps = buildCryptoMaps(currencies);
        setCryptoMaps(maps);
        loadProfiles(maps);
      })
      .catch((err) => {
        showHttpError(err);
        loadProfiles(cryptoMaps);
      });

    // Fetch configured payment gateways to guard Stripe/PayPal toggles
    api
      .getAllGateways(merchantId)
      .then((res) => {
        if (res.error !== 0) return;
        const gateways = res.payment_gateways || [];
        const stripe = gateways.find((g) => g.gatewayName === "Stripe");
        const paypal = gateways.find((g) => g.gatewayName === "Paypal");
        const kurvpay = gateways.find((g) => g.gatewayName?.toLowerCase() === "kurvpay");
        const netbilling = gateways.find(
          (g) => g.gatewayName.toLowerCase() === "netbilling"
        );
        const hms = gateways.find((g) => g.gatewayName === "HostMerchantServices");
        const nmi = gateways.find((g) => g.gatewayName === "NMI");
        const cardflo = gateways.find((g) => g.gatewayName === "cardflo");
        setGatewayConfig({
          stripe: !!(stripe?.clientId && stripe?.clientSecret),
          paypal: !!(paypal?.clientId && paypal?.clientSecret),
          kurvpay: !!(kurvpay?.clientId && kurvpay?.clientSecret),
          netbilling: !!(netbilling?.accountId && netbilling?.siteTag),
          hms: !!(hms?.clientId && hms?.clientSecret),
          nmi: !!(nmi?.clientId && nmi?.clientSecret),
          cardflo: !!(cardflo?.clientId && cardflo?.userName && cardflo?.password && cardflo?.cashierKey),
        });
      })
      .catch((err) => showHttpError(err));

    // Fetch available payment methods
    api
      .getPaymentMethods()
      .then((res) => {
        if (res.status && Array.isArray(res.data)) {
          setAvailableMethodIds(res.data.map((m) => m.id));
        }
      })
      .catch(() => {
        /* keep defaults */
      });
  }, [merchantId]);

  // ── Save single payment ──
  const handleSaveSingle = async (form) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setSaving(true);
    const payload = form.id
      ? mapSingleToUpdatePayload(form, merchantId, cryptoMaps.codeToId)
      : mapSingleToCreatePayload(form, merchantId, cryptoMaps.codeToId);
    try {
      const res = form.id
        ? await api.update(payload)
        : await api.create(payload);
      if (res.error !== "0") {
        showToast("error", res.message || "Failed to save profile.");
        return;
      }
      showToast(
        "success",
        res.message || (form.id ? "Profile updated." : "Profile created.")
      );
      setSingleMode("list");
      setSingleEditing(null);
      loadProfiles();
    } catch (err) {
      showHttpError(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──
  const handleDelete = (profile) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    showConfirm(
      "Delete Profile",
      `Are you sure you want to delete <strong>${profile.name}</strong>? This action cannot be undone.`,
      "warning",
      async () => {
        setDeletingId(profile.id);
        try {
          const res = await api.remove(profile.id, merchantId);
          if (res.error !== 0) {
            showToast("error", res.message || "Failed to delete profile.");
            return;
          }
          showToast("success", res.message || `"${profile.name}" deleted.`);
          loadProfiles();
        } catch (err) {
          showHttpError(err);
        } finally {
          setDeletingId(null);
        }
      }
    );
  };

  // ── Make default ──
  const handleMakeDefault = async (profile) => {
    if (isReadOnly) {
      showToast("error", "You need write access to perform this operation");
      return;
    }
    setMakingDefaultId(profile.id);
    try {
      const res = await api.markAsDefault(merchantId, profile.id, 1);
      if (res.error !== 0 && res.error !== "0") {
        showToast("error", res.message || "Failed to set as default.");
        return;
      }
      showToast("success", `"${profile.name}" is now the default profile.`);
      loadProfiles();
    } catch (err) {
      showHttpError(err);
    } finally {
      setMakingDefaultId(null);
    }
  };

  const isFormOpen = singleMode !== "list";

  return (
    <Fragment>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div style={S.pageWrapper}>
            {/* Page header */}
            <div style={S.headerRow}>
              <div>
                {isFormOpen && (
                  <span
                    onClick={() => {
                      setSingleMode("list");
                      setSingleEditing(null);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--btn-color)",
                      cursor: "pointer",
                      marginBottom: 8,
                      userSelect: "none",
                      opacity: 0.85,
                      transition: "opacity .15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = 1;
                      e.currentTarget.style.textDecoration = "underline";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = 0.85;
                      e.currentTarget.style.textDecoration = "none";
                    }}
                  >
                    <i
                      className="mdi mdi-arrow-left"
                      style={{ fontSize: 14 }}
                    />
                    Back to Payment Options
                  </span>
                )}
                <h2 style={S.pageTitle}>
                  {isFormOpen
                    ? singleMode === "add"
                      ? "Create Payment Options Profile"
                      : `Edit — ${singleEditing?.name}`
                    : "Payment Options"}
                </h2>
                {!isFormOpen && (
                  <div style={S.pageSubtitle}>
                    Configure payment methods available at checkout
                  </div>
                )}
              </div>
              {!isFormOpen && !loading && !isReadOnly && (
                <button
                  style={S.btnPrimary}
                  onClick={() => {
                    setSingleEditing(null);
                    setSingleMode("add");
                  }}
                >
                  <i className="mdi mdi-plus" />
                  Add Profile
                </button>
              )}
            </div>

            {/* Loading */}
            {loading && (
              <div style={{ ...S.card, padding: "40px", textAlign: "center" }}>
                <i
                  className="mdi mdi-loading mdi-spin"
                  style={{
                    fontSize: "28px",
                    color: "var(--btn-color)",
                    display: "block",
                    marginBottom: "12px",
                  }}
                />
                <div
                  style={{
                    fontSize: "13px",
                    color: "var(--text-color)",
                    opacity: 0.5,
                  }}
                >
                  Loading billing profiles…
                </div>
              </div>
            )}

            {/* ── PAYMENT PROFILES ── */}
            {!loading && (
              <>
                {(singleMode === "add" || singleMode === "edit") && (
                  <div style={S.card}>
                    <div style={S.cardHeader}>
                      <div
                        style={{
                          ...S.cardHeaderIcon,
                          background:
                            "linear-gradient(135deg, color-mix(in srgb, var(--btn-color) 15%, transparent), color-mix(in srgb, var(--btn-color) 5%, transparent))",
                          color: "var(--btn-color)",
                        }}
                      >
                        <i
                          className={
                            singleMode === "add"
                              ? "mdi mdi-plus"
                              : "mdi mdi-pencil"
                          }
                        />
                      </div>
                      <h4 style={S.cardHeaderTitle}>
                        {singleMode === "add"
                          ? "New Single Payment Profile"
                          : `Edit — ${singleEditing?.name}`}
                      </h4>
                    </div>
                    <div style={S.cardBody}>
                      <SinglePaymentForm
                        initial={singleEditing}
                        onSave={handleSaveSingle}
                        onCancel={() => {
                          setSingleMode("list");
                          setSingleEditing(null);
                        }}
                        saving={saving}
                        gatewayConfig={gatewayConfig}
                        availableMethodIds={availableMethodIds}
                      />
                    </div>
                  </div>
                )}
                {singleMode === "list" &&
                  (singleProfiles.length === 0 ? (
                    <EmptyState
                      icon="mdi-credit-card"
                      color="var(--btn-color)"
                      title="No payment profiles yet"
                      subtitle="Add a profile to configure payment methods available at checkout"
                      actionLabel={isReadOnly ? null : "Add Payment Option"}
                      onAction={isReadOnly ? null : () => {
                        setSingleEditing(null);
                        setSingleMode("add");
                      }}
                    />
                  ) : (
                    singleProfiles.map((p) => (
                      <SinglePaymentCard
                        key={p.id}
                        profile={p}
                        isDeleting={deletingId === p.id}
                        isMakingDefault={makingDefaultId === p.id}
                        isReadOnly={isReadOnly}
                        isTestMode={isTestMode}
                        onEdit={() => {
                          setSingleEditing(p);
                          setSingleMode("edit");
                        }}
                        onDelete={() => handleDelete(p)}
                        onMakeDefault={() => handleMakeDefault(p)}
                      />
                    ))
                  ))}
              </>
            )}
          </div>
          <Footer />
        </div>
      </div>
    </Fragment>
  );
}
